import { NodeTypes } from '../../constants/constants'
import { ParseError } from '../../errors'
import { minifyTokenStream } from '../../tokenizer/minifyTokenStream'
import { tokenize } from '../../tokenizer/tokenize'
import type { TemplateStringToken } from '../../tokenizer/token'
import { withSourceCodeInfo } from '../helpers'
import type { ParserContext } from '../ParserContext'
import type { AstNode, StringNode, TemplateStringNode } from '../types'
import { createParserContext, parseExpression } from './parseExpression'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Count newline characters in a string. */
function countNewlines(s: string): number {
  let count = 0
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\n') count++
  }
  return count
}

// ---------------------------------------------------------------------------
// Segment types
// ---------------------------------------------------------------------------

type LiteralSegment = { type: 'literal'; value: string }
type ExpressionSegment = { type: 'expression'; value: string; offset: number }
type DeferredSegment = { type: 'deferred'; value: string; dollarCount: number; offset: number }
export type Segment = LiteralSegment | ExpressionSegment | DeferredSegment

// ---------------------------------------------------------------------------
// Raw content scanner — splits template content into literal/expression spans
// ---------------------------------------------------------------------------

/**
 * Scan from `start` inside a `${...}` interpolation until the matching `}`.
 * Returns the expression source text (without the outer `${` and `}`) and
 * the number of characters consumed (including the closing `}`).
 */
function scanExpression(raw: string, start: number): { expr: string; consumed: number } {
  let i = start
  let expr = ''
  let depth = 1

  while (i < raw.length && depth > 0) {
    const c = raw[i]!

    if (c === '{') {
      depth++
      expr += c
      i++
    } else if (c === '}') {
      depth--
      if (depth > 0) {
        expr += c
      }
      i++ // consume the `}` in both cases
    } else if (c === '"') {
      const { str, consumed } = scanString(raw, i)
      expr += str
      i += consumed
    } else if (c === '\'') {
      const { str, consumed } = scanQuotedSymbol(raw, i)
      expr += str
      i += consumed
    } else if (c === '`') {
      const { str, consumed } = scanNestedTemplate(raw, i)
      expr += str
      i += consumed
    } else {
      expr += c
      i++
    }
  }

  return { expr, consumed: i - start }
}

function scanString(raw: string, start: number): { str: string; consumed: number } {
  let i = start + 1
  let str = '"'
  let escaping = false
  while (i < raw.length) {
    const c = raw[i]!
    str += c
    i++
    if (escaping) {
      escaping = false
    } else if (c === '\\') {
      escaping = true
    } else if (c === '"') {
      break
    }
  }
  return { str, consumed: i - start }
}

function scanQuotedSymbol(raw: string, start: number): { str: string; consumed: number } {
  let i = start + 1
  let str = '\''
  let escaping = false
  while (i < raw.length) {
    const c = raw[i]!
    str += c
    i++
    if (escaping) {
      escaping = false
    } else if (c === '\\') {
      escaping = true
    } else if (c === '\'') {
      break
    }
  }
  return { str, consumed: i - start }
}

/**
 * Scan a full nested template string starting at `start` (pointing at the opening backtick).
 * Handles ${...} spans inside the template recursively.
 */
function scanNestedTemplate(raw: string, start: number): { str: string; consumed: number } {
  let i = start + 1 // skip opening backtick
  let str = '`'

  while (i < raw.length) {
    const c = raw[i]!

    if (c === '`') {
      str += c
      i++
      break
    } else if (c === '$' && raw[i + 1] === '{') {
      str += '${'
      i += 2
      const { expr, consumed } = scanExpression(raw, i)
      str += `${expr}}`
      i += consumed
    } else {
      str += c
      i++
    }
  }

  return { str, consumed: i - start }
}

/**
 * Split the raw content of a template string (between the surrounding backticks)
 * into alternating literal and expression segments.
 */
export function splitSegments(raw: string): Segment[] {
  const segments: Segment[] = []
  let i = 0
  let literal = ''
  while (i < raw.length) {
    if (raw[i] === '$' && raw[i + 1] === '{') {
      // Deferred splice: $${ or $$${ — emit as a deferred segment
      // The first $ was already added to literal on a previous iteration
      if (literal.length > 0 && literal[literal.length - 1] === '$') {
        // Count how many $ signs precede the {
        let dollarCount = 1 // the current $
        while (literal.length >= dollarCount && literal[literal.length - dollarCount] === '$') {
          dollarCount++
        }
        dollarCount-- // don't count beyond the actual $ chars
        // Strip the leading $ signs from literal
        literal = literal.slice(0, -dollarCount)
        if (literal.length > 0) {
          segments.push({ type: 'literal', value: literal })
          literal = ''
        }
        i += 2 // skip ${
        const exprOffset = i
        const { expr, consumed } = scanExpression(raw, i)
        i += consumed
        segments.push({ type: 'deferred', value: expr, dollarCount: dollarCount + 1, offset: exprOffset })
        continue
      }

      // Normal splice — resolved by this template
      if (literal.length > 0) {
        segments.push({ type: 'literal', value: literal })
        literal = ''
      }
      i += 2 // skip `${`
      const exprOffset = i
      const { expr, consumed } = scanExpression(raw, i)
      i += consumed
      segments.push({ type: 'expression', value: expr, offset: exprOffset })
    } else {
      literal += raw[i]!
      i++
    }
  }

  if (literal.length > 0) {
    segments.push({ type: 'literal', value: literal })
  }

  return segments
}

// ---------------------------------------------------------------------------
// Public parser
// ---------------------------------------------------------------------------

export function parseTemplateString(ctx: ParserContext, token: TemplateStringToken): StringNode | TemplateStringNode {
  ctx.builder?.startNode('TemplateString')
  ctx.advance()
  const debugInfo = token[2]
  const resolvedSci = ctx.resolveTokenDebugInfo(debugInfo)

  // Strip surrounding backticks
  const raw = token[1].slice(1, -1)

  const segments = splitSegments(raw)

  // Empty template: ``, or single literal with no interpolation: `hello`
  if (segments.length === 0) {
    const node = withSourceCodeInfo([NodeTypes.Str, '', 0], debugInfo, ctx) as StringNode
    ctx.setNodeEnd(node[2])
    ctx.builder?.endNode()
    return node
  }
  if (segments.length === 1 && segments[0]!.type === 'literal') {
    const node = withSourceCodeInfo([NodeTypes.Str, segments[0]!.value, 0], debugInfo, ctx) as StringNode
    ctx.setNodeEnd(node[2])
    ctx.builder?.endNode()
    return node
  }

  // Build segment AST nodes
  const segmentNodes: (StringNode | AstNode)[] = []

  for (const segment of segments) {
    if (segment.type === 'literal') {
      if (segment.value.length === 0)
        continue
      segmentNodes.push(withSourceCodeInfo([NodeTypes.Str, segment.value, 0], debugInfo, ctx) as StringNode)
    } else {
      if (segment.value.trim().length === 0) {
        throw new ParseError('Empty interpolation in template string', resolvedSci)
      }
      // Re-tokenize and re-parse the expression.
      // Enable debug when the outer context has a sourceMap so that
      // interpolated expressions carry correct source positions.
      const hasDebug = ctx.sourceMap !== undefined
      const innerStream = tokenize(segment.value, hasDebug, resolvedSci?.filePath)
      const minified = minifyTokenStream(innerStream, { removeWhiteSpace: true })

      for (const t of minified.tokens) {
        if (t[0] === 'Error') {
          throw new ParseError(`Template string interpolation error: ${t[3]}`, resolvedSci)
        }
      }

      const innerCtx = createParserContext(minified, ctx.allocateId)
      const expr = parseExpression(innerCtx, 0)

      // Merge inner sourceMap positions into the outer sourceMap,
      // adjusting from segment-relative to file-absolute coordinates.
      if (ctx.sourceMap && innerCtx.sourceMap && debugInfo) {
        // Compute the absolute position of the expression content start
        // within the original file. `segment.offset` is where the expression
        // begins inside `raw` (the template content between backticks).
        const rawBefore = raw.substring(0, segment.offset)
        const newlinesBefore = countNewlines(rawBefore)
        const lastNewlineIdx = rawBefore.lastIndexOf('\n')

        // segStart = absolute [line, col] of the first char of the expression
        const segStartLine = debugInfo[0] + newlinesBefore
        const segStartCol = newlinesBefore === 0
          ? debugInfo[1] + 1 + segment.offset // +1 for the opening backtick
          : segment.offset - lastNewlineIdx - 1

        for (const [nodeId, innerPos] of innerCtx.sourceMap.positions) {
          ctx.sourceMap.positions.set(nodeId, {
            source: 0,
            start: [
              segStartLine + innerPos.start[0],
              innerPos.start[0] === 0 ? segStartCol + innerPos.start[1] : innerPos.start[1],
            ],
            end: [
              segStartLine + innerPos.end[0],
              innerPos.end[0] === 0 ? segStartCol + innerPos.end[1] : innerPos.end[1],
            ],
            ...(innerPos.structuralLeaf ? { structuralLeaf: true } : {}),
          })
        }
      }

      segmentNodes.push(expr)
    }
  }

  const node = withSourceCodeInfo([NodeTypes.TmplStr, segmentNodes, 0], debugInfo, ctx) as TemplateStringNode
  ctx.setNodeEnd(node[2])
  ctx.builder?.endNode()
  return node
}
